import { ChevronDown } from 'lucide-react'
import { usePathname } from 'next/navigation'

import { Button } from '~/components/component-library/Button'
import { SidebarRowButton } from '~/components/component-library/internal/Sidebar/components/SidebarRowButton'
import { useExpandedState } from '~/components/component-library/internal/Sidebar/hooks'
import { ComponentCategory } from '~/components/component-library/internal/Sidebar/types'
import { cn } from '~/utils/tailwind-utils'

export const SidebarCollapsible = (
  props: ComponentCategory & { forceOpen?: boolean },
) => {
  const storageKey = 'component-library:sidebar-expanded:' + props.name
  const { isExpanded, toggleExpanded } = useExpandedState(true, storageKey)
  const pathname = usePathname()

  // while searching, keep every matching category open regardless of its
  // saved collapsed state
  const isOpen = props.forceOpen || isExpanded

  const activeItem = props.components.find(
    ({ id }) => id && pathname.endsWith(id + '/'),
  )

  return (
    <div className="flex min-w-0 flex-col">
      <Button
        variant="tertiary"
        size="small"
        fullWidth
        isDisabled={props.forceOpen}
        prefix={<props.icon className="text-secondary" />}
        suffix={
          <ChevronDown
            className={cn(
              'size-4 text-secondary transition-transform duration-100',
              { 'rotate-180': isOpen },
            )}
          />
        }
        onClick={toggleExpanded}
        className="font-normal"
      >
        <span className="flex-1 truncate text-left text-secondary">
          {props.name}
        </span>
      </Button>
      {/* grid-rows 0fr -> 1fr animates the height; minmax(0,1fr) caps the
          column to the sidebar width so rows truncate instead of overflowing */}
      <div
        className={cn(
          'grid grid-cols-[minmax(0,1fr)] overflow-hidden transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none',
          {
            'grid-rows-[0fr]': !isOpen,
            'grid-rows-[1fr]': isOpen,
          },
        )}
      >
        <div
          className={cn(
            'flex min-h-0 min-w-0 flex-col gap-0.5 transition-[opacity,transform,padding] duration-200 ease-out motion-reduce:transition-none',
            {
              'pointer-events-none -translate-y-1 opacity-0': !isOpen,
              'translate-y-0 pb-1.5 pt-1 opacity-100': isOpen,
            },
          )}
          // inert when collapsed: drops the hidden rows from tab order and the
          // a11y tree so focus skips to the next category. Must be undefined
          // (not false) when open, or the boolean attribute stays applied.
          inert={isOpen ? undefined : true}
        >
          {props.components.map((item) => (
            <SidebarRowButton
              key={item.id}
              isActive={activeItem?.id === item.id}
              {...item}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
